import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Message } from '@copilotkit/shared';
import { useCopilotChatHeadless_c } from '@copilotkit/react-core';
import { useCopilotKit } from '@copilotkit/react-core/v2';
import { useAppSelector, useAppDispatch } from '@/redux/store';
import { useCopilotKitHydration } from './useCopilotKitHydration';
import { useCopilotKitSync } from './useCopilotKitSync';
import {
  setLastToolset,
  setNeedsHistoryRestore,
  clearConversation,
} from '@/redux/slices/copilotKitChatSlice';
import { useLazyGetConversationMessagesQuery } from '@/redux/slices/gigawattBackendApiSlice';
import {
  setFeedbackBulk,
  setIdMapping,
  markIdsResolved,
} from '@/redux/slices/messageFeedbackSlice';
import {
  WELCOME_MESSAGE_ID,
  type FeedbackReason,
  type MessageFeedbackEntry,
} from '@/types/aiChat';
import { TOOL_NAMES } from '@/helpers/aiChat/tools';

// Backend returns per-message feedback as extra fields directly on each
// history row (per TF-588 tech spec section 5) rather than as part of
// CopilotKit's typed Message shape — camelCased on the wire, matching the
// AG-UI/CopilotKit protocol convention the rest of the message shape already
// uses (e.g. toolCalls), even though this slice's baseQuery otherwise leaves
// responses uncamelized. We must read these off the raw history response
// before calling setMessages — CopilotKit's own normalization may strip
// unrecognized properties, so capturing them after setMessages would risk
// silently losing previously-submitted feedback on reload.
type HistoryMessageWithFeedback = Message & {
  feedbackRating?: 'up' | 'down' | null;
  feedbackReasons?: FeedbackReason[] | null;
};

// CopilotKit's headless hook appends a placeholder assistant message (id
// `coagent-state-render-<agent>-<run>`) while the agent is running — which
// includes the `agent/connect` it issues on mount. It is not conversation
// content, so it must not count as "messages are present".
const COAGENT_STATE_RENDER_ID_PREFIX = 'coagent-state-render';

const TOOL_REQUIRES_APPROVAL_EVENT = 'tool_requires_approval';
const MESSAGE_IDS_PERSISTED_EVENT = 'message_ids_persisted';

// Tools with dedicated useHumanInTheLoop renderers that manage their own approval
// flow. The backend still emits tool_requires_approval for these, but the frontend
// handles the approval entirely via their render-and-wait UI — so we must NOT also
// surface the generic backend-deferred approval prompt (pendingToolApproval) for them.
const FRONTEND_HANDLED_TOOLS = new Set<string>([
  TOOL_NAMES.NAVIGATE_TO_PAGE,
  TOOL_NAMES.ADD_PRODUCTS_TO_CART,
  TOOL_NAMES.OPEN_SUPPORT_TICKET,
  TOOL_NAMES.CONNECT_APP_INTEGRATION,
]);

export type PendingToolApproval = {
  toolCallId: string;
  toolCallName?: string;
};

const isToolRequiresApprovalEvent = (
  value: unknown
): value is PendingToolApproval => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toolCallId' in value &&
    typeof (value as Record<string, unknown>).toolCallId === 'string'
  );
};

const isMessageIdsPersistedEvent = (
  value: unknown
): value is { messageIds: string[] } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as Record<string, unknown>).messageIds) &&
    (value as { messageIds: unknown[] }).messageIds.every(
      id => typeof id === 'string'
    )
  );
};

export const WELCOME_MESSAGE: Message = {
  id: WELCOME_MESSAGE_ID,
  role: 'assistant',
  content:
    "Hello! 👋 I'm Gigawatt, your IT support specialist. I'm here to help you with any IT-related questions or issues you might have—whether it's about devices, software, employee management, security, or anything else tech-related.\n\nHow can I assist you today?",
};

// Translation key (namespace 'ai-chat') for the Getting Started variant of the
// welcome message. Kept as a key rather than resolved text so every consumer
// (this hook, CopilotKitAgentContext, Dashboard) reads it via t() and reacts
// to language changes the same way.
export const GS_WELCOME_MESSAGE_KEY = 'gsWelcomeMessage';

type UseCopilotKitAgentOptions = {
  /**
   * When provided, replaces the default welcome message content shown on a fresh conversation.
   * Only takes effect when messages are empty (i.e. the conversation hasn't started yet).
   */
  initialMessage?: string;
};

/**
 * Hook using useCopilotChatHeadless_c for headless chat UI with Redux persistence
 *
 * This hook provides direct access to CopilotKit's messages array and automatically:
 * - Hydrates messages from Redux on mount
 * - Shows welcome message when starting fresh
 * - Syncs messages to Redux after agent finishes generating
 * - Recovers from CopilotKit resets (e.g., React StrictMode remounts)
 *
 * @param namespaceKey - The namespace key for this chat instance
 * @param agentState - Optional full agent state to sync (user context, location, etc.)
 * @param options - Optional configuration for welcome message behavior
 * @returns CopilotKit chat methods and state
 */
export const useCopilotKitAgent = (
  namespaceKey: string,
  agentState?: Record<string, unknown>,
  options?: UseCopilotKitAgentOptions
) => {
  const { initialMessage } = options ?? {};
  const dispatch = useAppDispatch();
  const { t } = useTranslation('ai-chat');

  const welcomeMessage = useMemo<Message>(
    () => ({
      id: WELCOME_MESSAGE_ID,
      role: 'assistant',
      content: initialMessage ?? t('welcomeMessage'),
    }),
    [t, initialMessage]
  );
  const welcomeMessageContent = welcomeMessage.content;

  // Semantic fingerprint of the agent state we own — used so the sync effect
  // depends on content, not a fresh object reference every parent render.
  const agentStateKey = useMemo(
    () => JSON.stringify(agentState ?? {}),
    [agentState]
  );

  // Get persisted messages from Redux
  const reduxMessages = useAppSelector(state => {
    return state.copilotKitChat.conversations[namespaceKey]?.messages || [];
  });

  // True while a new-chat API call is in-flight (set synchronously by useNewChatAction)
  const isCreatingNewConversation = useAppSelector(
    state =>
      state.copilotKitChat.conversations[namespaceKey]
        ?.isCreatingNewConversation ?? false
  );

  const needsHistoryRestore = useAppSelector(
    state =>
      state.copilotKitChat.conversations[namespaceKey]?.needsHistoryRestore ??
      false
  );

  const conversationId = useAppSelector(
    state => state.copilotKitChat.conversations[namespaceKey]?.conversationId
  );

  const organizationId = useAppSelector(
    state => state.loggedUser?.organizationId
  );

  const [fetchConversationMessages] = useLazyGetConversationMessagesQuery();

  // Get lastToolset for subscription monitoring (to detect backend updates)
  const lastToolset = useAppSelector(
    state =>
      state.copilotKitChat.conversations[namespaceKey]?.lastToolset ?? null
  );

  const { copilotkit } = useCopilotKit();

  // Use headless chat hook - requires publicApiKey in CopilotKitProvider
  // suggestions: 'manual' so we control them via setSuggestions (page-specific quick prompts)
  const {
    messages,
    sendMessage,
    setMessages,
    isLoading,
    isAvailable,
    threadId,
    agent,
    setSuggestions,
  } = useCopilotChatHeadless_c({
    suggestions: 'manual',
  });

  const [pendingToolApproval, setPendingToolApproval] =
    useState<PendingToolApproval | null>(null);

  const [runError, setRunError] = useState<string | null>(null);

  // Frontend-handled tools (navigate_to_page, etc.) manage their approval via
  // useHumanInTheLoop and don't go through pendingToolApproval. We derive a
  // pending state from the message list so the chat input can still be blocked
  // while the user has an unresolved frontend tool_call — otherwise the user
  // can type a new prompt, leaving the tool_use orphaned and triggering a
  // Bedrock 400 on the next turn (TF-453).
  const hasPendingFrontendToolApproval = useMemo(() => {
    if (isLoading) return false;
    const answeredToolCallIds = new Set<string>();
    for (const msg of messages) {
      if (msg.role === 'tool' && !!msg.toolCallId) {
        answeredToolCallIds.add(msg.toolCallId);
      }
    }
    return messages.some(
      msg =>
        msg.role === 'assistant' &&
        !!msg.toolCalls &&
        msg.toolCalls.some(
          call =>
            FRONTEND_HANDLED_TOOLS.has(call.function?.name ?? '') &&
            call.id !== undefined &&
            !answeredToolCallIds.has(call.id)
        )
    );
  }, [messages, isLoading]);

  // Clear any pending approval when the user starts a new chat. Without this,
  // pendingToolApproval stays set (onRunStartedEvent never fires because the
  // disabled input prevents the user from triggering a new run), leaving the
  // input permanently disabled in the fresh conversation.
  useEffect(() => {
    if (isCreatingNewConversation) {
      setPendingToolApproval(null);
      setRunError(null);
    }
  }, [isCreatingNewConversation]);

  const respondToApproval = useCallback(
    (toolCallId: string, approved: boolean) => {
      if (!agent) return;
      setPendingToolApproval(null);
      // Resume through copilotkit.runAgent() — NOT the bare agent.runAgent().
      // The bare call skips copilotkit's processAgentResult step, which is what
      // (a) attaches the frontend tools, (b) executes handler-based frontend
      // tools, and (c) drives useHumanInTheLoop render-and-wait (e.g. the
      // navigate_to_page approval UI). Without it, a frontend tool called in
      // the resumed turn (right after a backend approval) never shows its
      // approval prompt and the chip hangs in the "running" state.
      //
      // copilotkit.runAgent() takes no per-call forwardedProps, so the deferred
      // approval is passed via the global properties for the duration of this
      // run, then restored.
      const baseProperties = copilotkit.properties;
      copilotkit.setProperties({
        ...baseProperties,
        deferredToolCalls: { [toolCallId]: approved },
      });
      void copilotkit
        .runAgent({ agent })
        .finally(() => copilotkit.setProperties(baseProperties));
    },
    [agent, copilotkit]
  );

  // Track the last agent state we set to prevent redundant updates
  const lastSetStateRef = useRef<string>('');

  // Track last toolset in a ref to prevent subscription recreation
  const lastToolsetRef = useRef<string | null>(lastToolset);

  // Update ref when lastToolset changes
  useEffect(() => {
    lastToolsetRef.current = lastToolset;
  }, [lastToolset]);

  // Sync agent state when it changes.
  // Always merges with existing agent.state to preserve backend-managed fields.
  //
  // Compare only *our* agentState slice before calling setState — NOT the merged
  // payload. agent.state includes backend-managed fields that can change on every
  // STATE_DELTA (e.g. lastToolset) even when our slice is unchanged; comparing the
  // merged JSON caused setState → backend echo → merged JSON differs → loop (TF-723).
  useEffect(() => {
    // If agent becomes unavailable, clear the cache so we re-set state when it comes back
    // This handles React StrictMode unmount/remount cycles in development
    if (!isAvailable) {
      lastSetStateRef.current = '';
      return;
    }

    if (agent && isAvailable && agentState) {
      if (agentStateKey === lastSetStateRef.current) {
        return;
      }

      lastSetStateRef.current = agentStateKey;

      const currentBackendState = agent.state || {};
      agent.setState({ ...currentBackendState, ...agentState });
    }
  }, [agent, isAvailable, agentState, agentStateKey]);

  // Subscribe to agent events to detect backend state updates
  useEffect(() => {
    if (!agent || !isAvailable) return;

    const subscription = agent.subscribe({
      onEvent: ({ event }: { event: Record<string, unknown> }) => {
        // Handle state updates (STATE_DELTA or STATE_SNAPSHOT)
        if (event.type === 'STATE_DELTA' || event.type === 'STATE_SNAPSHOT') {
          const newState = event.state || event.snapshot;
          const newToolset = (newState as Record<string, unknown>)?.lastToolset;

          // Use ref to check current value without causing effect re-runs
          if (newToolset && newToolset !== lastToolsetRef.current) {
            dispatch(
              setLastToolset({
                namespaceKey,
                toolset: newToolset as string,
              })
            );
          }
        }
      },
      onCustomEvent: ({ event }) => {
        if (event.name === TOOL_REQUIRES_APPROVAL_EVENT) {
          if (!isToolRequiresApprovalEvent(event.value)) return;
          const { toolCallId, toolCallName } = event.value;
          // Frontend-handled tools render their own approval via useHumanInTheLoop;
          // don't also show the generic approval prompt for them.
          if (toolCallName && FRONTEND_HANDLED_TOOLS.has(toolCallName)) return;
          setPendingToolApproval({
            toolCallId,
            toolCallName,
          });
          return;
        }

        if (event.name === MESSAGE_IDS_PERSISTED_EVENT) {
          if (!isMessageIdsPersistedEvent(event.value)) return;
          const { messageIds } = event.value;
          if (messageIds.length === 0) return;

          // Read directly off `agent.messages` rather than the `messages`
          // state/a React ref mirroring it — AG-UI's event pipeline updates
          // `agent.messages` synchronously before dispatching this custom
          // event, but React's render+effect cycle is not guaranteed to
          // have flushed a ref update by the time this callback runs, which
          // could otherwise read a one-render-stale array.
          //
          // The backend persists (and counts) every message in the turn,
          // including tool-call/tool-result messages, not just user/assistant
          // ones — so we must slice the *unfiltered* array to stay aligned
          // with `messageIds` by tail position, and only filter to
          // user/assistant afterwards when building the id map below (only
          // those are ever eligible for feedback; see MessagesList, which
          // renders nothing else).
          const allMessages = agent.messages ?? [];
          // AG-UI turns are strictly sequential (the input is disabled while a
          // run is in flight), so the newest entries in the live messages array
          // are always exactly this turn's messages, in the same emission order
          // the backend used to build `messageIds` (see the backend's
          // ChatMessage.to_ag_ui_message_history comment) — including the user's
          // own just-sent message, which the backend persists and counts too.
          // Matching by tail position (rather than diffing against a pre-run
          // snapshot) avoids having to special-case "was this message already
          // there before the run started".
          const newMessages = allMessages.slice(-messageIds.length);

          // Cardinality must match exactly — if it doesn't, we can't safely map
          // temp ids to persisted ids without risking assigning the wrong
          // persisted id to the wrong message, so skip the mapping entirely
          // rather than guess. The affected message(s) simply stay
          // non-interactive.
          if (newMessages.length !== messageIds.length) {
            return;
          }

          // Deliberately NOT applied via setMessages — mutating an existing
          // message's id in CopilotKit's own messages array was found to
          // trigger CopilotKit's internal "message changed" handling, which
          // attempts to regenerate/continue the run and crashes on a
          // browser-incompatible `process.env` reference. Instead we record
          // the mapping separately; MessageFeedbackActions resolves the
          // effective id to use via idMap[messageId] ?? messageId.
          const idMap: Record<string, string> = {};
          newMessages.forEach((message, index) => {
            if (message.role === 'user' || message.role === 'assistant') {
              idMap[message.id] = messageIds[index];
            }
          });
          dispatch(setIdMapping(idMap));
        }
      },
      onRunStartedEvent: () => {
        setPendingToolApproval(null);
      },
      onRunFinishedEvent: () => {
        setRunError(null);
      },
      onRunErrorEvent: () => {
        setPendingToolApproval(null);
        setRunError(t('errors.communicationError'));
      },
    });

    return () => subscription?.unsubscribe?.();
  }, [agent, isAvailable, namespaceKey, dispatch, t]);

  // Track syncing state to prevent hydration loops
  const isSyncingRef = useRef(false);

  // Show the welcome message when starting fresh or after a new chat clears messages.
  // Skipped when needsHistoryRestore is true — the restore effect handles the initial message.
  useEffect(() => {
    if (
      isAvailable &&
      !isLoading &&
      messages.length === 0 &&
      !needsHistoryRestore
    ) {
      setMessages([welcomeMessage]);
    }
  }, [
    isAvailable,
    isLoading,
    messages.length,
    needsHistoryRestore,
    setMessages,
    welcomeMessage,
  ]);

  // Keep the welcome message in sync with the active language (or, for the
  // Getting Started flow, with a changed initialMessage). The welcome effect
  // above only fires while messages are empty, so once the bubble is shown it
  // stays frozen in whatever text it was first rendered with. Here we
  // re-apply the freshly translated welcomeMessage while the chat is still
  // untouched (exactly one message, the welcome message).
  //
  // Depend on message *content*, not the full messages array — CopilotKit
  // returns a new array reference on every setMessages call, which would
  // otherwise retrigger this effect indefinitely.
  const soloWelcomeContent =
    messages.length === 1 && messages[0]?.id === WELCOME_MESSAGE_ID
      ? typeof messages[0].content === 'string'
        ? messages[0].content
        : ''
      : null;

  useEffect(() => {
    if (soloWelcomeContent === null) return;
    if (soloWelcomeContent === welcomeMessageContent) return;
    setMessages([welcomeMessage]);
  }, [soloWelcomeContent, welcomeMessageContent, setMessages, welcomeMessage]);

  // Restore conversation history from the backend when the conversationId was
  // loaded from localStorage (needsHistoryRestore = true) rather than freshly created.
  useEffect(() => {
    if (!isAvailable || isLoading || !needsHistoryRestore) return;
    if (!organizationId || !conversationId) return;

    let cancelled = false;

    const doRestore = async () => {
      try {
        const history = await fetchConversationMessages({
          organizationId,
          conversationId,
        }).unwrap();

        if (cancelled) return;

        const feedbackMap: Record<string, MessageFeedbackEntry> = {};
        (history as HistoryMessageWithFeedback[]).forEach(item => {
          // Include explicit nulls (backend-side feedback clear) so they
          // overwrite any stale non-null rating already in Redux — only
          // skip when the field is absent entirely (older history rows).
          if (item.feedbackRating !== undefined) {
            feedbackMap[item.id] = {
              rating: item.feedbackRating,
              reasons: item.feedbackReasons ?? [],
            };
          }
        });
        if (Object.keys(feedbackMap).length > 0) {
          dispatch(setFeedbackBulk(feedbackMap));
        }
        // Every message loaded from history already has a backend-persisted
        // id (unlike a freshly-streamed message, which starts out with a
        // temporary id until MESSAGE_IDS_PERSISTED_EVENT resolves it) — mark
        // them resolved so their feedback controls aren't disabled.
        if (history.length > 0) {
          dispatch(markIdsResolved(history.map(item => item.id)));
        }

        setMessages(history.length > 0 ? history : [welcomeMessage]);
        // needsHistoryRestore is cleared by the messages.length effect below,
        // only after CopilotKit has applied setMessages — avoiding a race where
        // a synchronous Redux update triggers the welcome message before history arrives.
      } catch {
        if (cancelled) return;

        // Conversation not found or expired — clear the stale ID so
        // CopilotKitProvider creates a fresh conversation on re-render.
        // organizationId is guaranteed non-null by the early return above.
        localStorage.removeItem(`gigawatt_${organizationId}_conversation_id`);
        dispatch(clearConversation({ namespaceKey }));
        // clearConversation already resets needsHistoryRestore to false.
      }
    };

    doRestore();

    return () => {
      cancelled = true;
    };
    // Intentionally omitted deps:
    //  - organizationId, conversationId: stable for the session (set once from localStorage/API)
    //  - fetchConversationMessages: stable RTK lazy query reference
    //  - dispatch, namespaceKey: stable Redux/constant refs
    // The only intended trigger is needsHistoryRestore flipping to true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailable, isLoading, needsHistoryRestore]);

  // Clear needsHistoryRestore only once messages are confirmed in CopilotKit state.
  // This prevents the welcome message effect from firing in the window between
  // dispatch(setNeedsHistoryRestore(false)) and CopilotKit flushing setMessages(history).
  const hasConversationMessages = messages.some(
    message => !message.id.startsWith(COAGENT_STATE_RENDER_ID_PREFIX)
  );
  useEffect(() => {
    if (needsHistoryRestore && hasConversationMessages) {
      dispatch(setNeedsHistoryRestore({ namespaceKey, value: false }));
    }
  }, [needsHistoryRestore, hasConversationMessages, dispatch, namespaceKey]);

  // Handle hydration from Redux on page refresh (currently dormant — see hook for details)
  useCopilotKitHydration({
    isAvailable,
    isLoading,
    messages,
    reduxMessages,
    setMessages,
    isSyncingRef,
  });

  // Handle syncing messages to Redux
  useCopilotKitSync({
    namespaceKey,
    messages,
    isLoading,
    isSyncingRef,
    isCreatingNewConversation,
  });

  const sendMessageAndClearError: typeof sendMessage = useCallback(
    (...args) => {
      setRunError(null);
      return sendMessage(...args);
    },
    [sendMessage]
  );

  return {
    messages,
    sendMessage: sendMessageAndClearError,
    setMessages,
    isLoading,
    isAvailable,
    threadId,
    isCreatingNewConversation,
    needsHistoryRestore,
    setSuggestions,
    pendingToolApproval,
    hasPendingFrontendToolApproval,
    respondToApproval,
    runError,
    // Exposed so callers can append messages via agent.addMessage/addMessages
    // instead of setMessages when replaying the full history isn't safe — see
    // CopilotKitAgentContext.tsx's upgrade-nudge guard for why.
    agent,
  };
};
